<template>
  <div class="uk-flex-center uk-margin-medium-top" uk-grid>
    <ErrorModal id="AccountErrorModal" message="There was an internal error updating your information. Please try again or contact support." />
    <div class="uk-width-1-2@xl uk-width-2-3@m uk-width-1-1@s">
      <Card>
        <template v-slot:header>
            <h2>My Account</h2>
        </template>

        <template v-slot:body>
          <form class="uk-form-horizontal uk-grid-row-small" uk-grid v-on:submit.prevent="submit_edit_account">

            <div class="uk-width-1-1">
              <label class="uk-form-label uk-text-bold" for="name">Name</label>
              <div class="uk-form-controls">
                <input class="uk-input" type="text" name="name" v-model="edit_full_name" :disabled="!edit_information" required>
              </div>
            </div>

            <div class="uk-width-1-1">
              <label class="uk-form-label uk-text-bold" for="email">Email</label>
              <div class="uk-form-controls">
                <input class="uk-input" type="email" name="email" v-model="edit_email" :disabled="!edit_information" required>
              </div>
            </div>

            <div class="uk-width-1-1">
              <label class="uk-form-label uk-text-bold" for="address">Address</label>
              <div class="uk-form-controls">
                <input class="uk-input" type="text" name="address" v-model="edit_address" :disabled="!edit_information" required>
              </div>
            </div>

            <div class="uk-width-1-1">
              <label class="uk-form-label uk-text-bold" for="city">City</label>
              <div class="uk-form-controls">
                <input class="uk-input" type="text" name="city" v-model="edit_city" :disabled="!edit_information" required>
              </div>
            </div>

            <div class="uk-width-1-1">
              <label class="uk-form-label uk-text-bold" for="state">State</label>
              <div class="uk-form-controls">
                <!-- disabled select still has arrows and can't disable in a good cross-browser way, so we're just hiding the element -->
                <select class="uk-select" type="text" name="state" :value="state" v-model="edit_state" v-if="edit_information" required>
                  <option v-for="(option, index) in possibleStates" :key="index" :value="option.code">{{ option.name }}</option>
                </select>
                <input class="uk-input" type="text" name="state" :value="state" v-else disabled>
              </div>
            </div>

            <div class="uk-width-1-1">
              <label class="uk-form-label uk-text-bold" for="country">Country</label>
              <div class="uk-form-controls">
                <select class="uk-select" type="text" name="country" :value="country" v-model="edit_country" v-if="edit_information" required>
                  <option v-for="(option, index) in possibleCountries" :key="index" :value="option.alpha2">{{ option.name }}</option>
                </select>
                <input class="uk-input" type="text" name="country" :value="country" v-else disabled>
              </div>
            </div>

            <div class="uk-width-1-1">
              <label class="uk-form-label uk-text-bold" for="zip">Zip</label>
              <div class="uk-form-controls">
                <input class="uk-input" type="text" name="zip" v-model="edit_zip" :disabled="!edit_information" required>
              </div>
            </div>

          </form>
        </template>

        <template v-slot:footer>
          <div uk-grid>
            <div class="uk-float-left uk-width-1-3@s uk-width-1-4@l">
              <BitButton color="inverse" @click.native="edit_information = true" v-if="!edit_information">
                <span uk-icon="icon: pencil;" class="buttonIcon"></span>
                Edit
              </BitButton>
              <BitButton color="red" @click.native="discard_edits" v-else>
                <span uk-icon="icon: trash;" class="buttonIcon"></span>
                Discard
              </BitButton>
            </div>
            <div class="uk-flex-last uk-flex-right uk-width-1-3@s uk-width-1-4@l">
              <BitButton v-if="edit_information && !spin" color="green" type="submit" @click.native="submit_edit_account">
                <span uk-icon="icon: check;" class="buttonIcon"></span>
                Save
              </BitButton>
              <BitButton v-if="edit_information && spin" color="green" type="submit" disabled><div uk-spinner></div></BitButton>
            </div>
          </div>
        </template>
      </Card>
    </div>
  </div>
</template>

<script>
import BitButton from '@/components/common/button.vue';
import Card from '@/components/common/card.vue';
import ErrorModal from '@/components/portal/errormodal.vue';

import UIkit from 'uikit';
import Axios from '@/store/axios'
var Promise = require('bluebird')

import { mapActions, mapGetters, mapState } from "vuex";

export default {
  name: 'MyAccount',
  components: {
    BitButton,
    Card,
    ErrorModal
  },
  data() {
    return {
      edit_information: false,
      edit_full_name: null,
      edit_email: null,
      edit_address: null,
      edit_city: null,
      edit_state: null,
      edit_country: null,
      edit_zip: null,
      possibleCountries: [],
      possibleStates: [],
      spin: false
    }
  },
  computed: {
    ...mapState('user', ['id', 'user_id', 'email', 'address', 'city', 'state', 'country', 'zip']),
    ...mapGetters('user', ['full_name']),
  },
  created() {
    this.discard_edits();
    this.GET_ACCOUNT_INFORMATION(this.id);
  },
  methods: {
    ...mapActions('user', ['GET_ACCOUNT_INFORMATION', 'UPDATE_ACCOUNT_INFORMATION']),
    capitalizeFirstLetter(str) {
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
    get_possible_countries() {
      return Promise.try( () => {
        return Axios.get(`/billing/countries`)
      }).then( (countries_response) => {
        return countries_response.data
      })
    },
    get_possible_states(country_code) {
      return Promise.try( () => {
        return Axios.get(`/billing/states/${country_code}`)
      }).then( (states_response) => {
        return states_response.data
      })
    },
    discard_edits() {
      this.initialize_selectors();
      this.edit_information = false; // reset our UI
      // so we can reuse this to reset our edit as well
      this.edit_full_name = this.full_name;
      this.edit_email = this.email;
      this.edit_address = this.address;
      this.edit_city = this.city;
      this.edit_zip = this.zip;
    },
    initialize_selectors() {
      // INITIALIZE COUNTRY/STATE SELECTORS
      this.get_possible_countries().then( (countries) => {
        this.possibleCountries = countries;
      })
      if(this.country) {
        // if user has a country defined, get the states for that country prepped and set the default country
        this.get_possible_states(this.country).then( (states) => {
          this.possibleStates = states;
          this.edit_country = this.country;
        })
      } else {
        // else load US states as default
        this.get_possible_states('US').then( (states) => {
          this.possibleStates = states;
        })
      }
      // if user has state defined, set default state for select binds
      if(this.state) {
        this.edit_state = this.state.toUpperCase(); // fix for uppercase issue in matching on the selectors
      }
    },
    submit_edit_account() {
      this.spin = true;
      // gotta be a better way to do this, but this is all I got for now – on a time crunch
      let user_edit = {}
      if(this.edit_full_name) {
        var fullName = this.edit_full_name.split(' ');
        user_edit.first_name = this.capitalizeFirstLetter(fullName[0]),
        user_edit.last_name = this.capitalizeFirstLetter(fullName[fullName.length - 1])
      }
      if(this.edit_email) user_edit.email = this.edit_email;
      if(this.edit_address) user_edit.address = this.edit_address;
      if(this.edit_city) user_edit.city = this.edit_city;
      if(this.edit_state) user_edit.state = this.edit_state;
      if(this.edit_country) user_edit.country = this.edit_country;
      if(this.edit_zip) user_edit.zip = this.edit_zip;

      this.UPDATE_ACCOUNT_INFORMATION(user_edit).then( () => {
        this.edit_information = false;
        this.spin = false;
      }).catch( () => {
        this.spin = false;
        // for now we are just showing a static error message. in the future this will be expanded to include detailed error information, which starts with better error capturing on our API
        UIkit.modal('#AccountErrorModal').show();
      })
    }
  },
  watch: {
    edit_country: function (country) {
      this.get_possible_states(country).then( (states) => {
        this.possibleStates = states;
      })
    }
  }
}
</script>

<style scoped lang="less">
@import "../../../assets/less/theme.less";

.uk-form-horizontal {
  .uk-form-label {
    width: 100px;
  }
  .uk-form-controls {
    margin-left: 115px;
  }
}

.uk-input:disabled { // normalize disabled view
  color: #666;
  border: none;
}

.buttonIcon {
  margin-left: -15px;
}
</style>
